import { t, Trans } from "@lingui/macro";
import QRCode from "qrcode.react";
import { useEffect, useState } from "react";
import { Connector as ConnectorWagmi, useConnect, useConnectors } from "wagmi";

import ChevronLeft from "assets/icons/ChevronLeft";
import CloseIcon from "assets/icons/CloseIcon";
import { useGlobalContext } from "context/GlobalContext/GlobalContextProvider";
import useWallet from "lib/wallets/useWallet";

import Button from "components/Button/Button";
import Loader from "components/Common/Loader";
import Modal from "components/Modal/Modal";
import Typography from "components/Typography/Typography";

import LiquidImg from "img/liquid.png";

import "./ConnectWalletModal.scss";

interface Connector extends ConnectorWagmi {
  rkDetails: {
    name: string;
    downloadUrls: {
      browserExtension?: string;
      chrome?: string;
      android?: string;
      ios?: string;
      mobile?: string;
      qrCode?: string;
    };
    iconUrl: () => Promise<string>;
    id: string;
    installed: boolean;
    isWalletConnectModalConnector?: boolean;
    qrCode: {
      getUri: (a: string) => string;
    };
  };
}

const getDownloadUrl = (connector: Connector) => {
  if (connector?.rkDetails?.id === "safe") {
    return "https://safe.global/wallet";
  }
  const { downloadUrls } = connector.rkDetails;

  if (/android/i.test(navigator.userAgent)) {
    return (
      downloadUrls?.android ||
      downloadUrls?.mobile ||
      downloadUrls?.qrCode ||
      downloadUrls?.chrome ||
      downloadUrls?.browserExtension ||
      ""
    );
  }
  if (/iphone|ipad|ipod/i.test(navigator.userAgent)) {
    return (
      downloadUrls?.ios ||
      downloadUrls?.mobile ||
      downloadUrls?.qrCode ||
      downloadUrls?.chrome ||
      downloadUrls?.browserExtension ||
      ""
    );
  }
  return downloadUrls?.browserExtension || downloadUrls?.chrome || downloadUrls?.qrCode || downloadUrls?.mobile || "";
};

const ConnectWalletModal = () => {
  const { connectWalletModalOpen, setConnectWalletModalOpen } = useGlobalContext();
  const connectors = useConnectors();
  const { account } = useWallet();

  const [isConnecting, setIsConnecting] = useState(false);

  const [qrWalletConnect, setQrWalletConnect] = useState<{
    connector: Connector;
    icon: string;
    uri: string;
  } | null>(null);

  useEffect(() => {
    if (!connectWalletModalOpen) {
      setQrWalletConnect(null);
      setIsConnecting(false);
    }
  }, [connectWalletModalOpen]);

  // Close connect modal once the wallet is connected (SIWE prompt follows).
  useEffect(() => {
    if (account && connectWalletModalOpen) {
      setConnectWalletModalOpen(false);
    }
  }, [account, connectWalletModalOpen, setConnectWalletModalOpen]);

  return (
    <Modal
      className="ConnectWalletModal"
      isVisible={connectWalletModalOpen && !account}
      setIsVisible={setConnectWalletModalOpen}
      label={t`Connect Wallet`}
      zIndex={60}
      clearUI
    >
      <div className="ConnectWalletModal-introduction">
        <img src={LiquidImg} alt="Liquid Logo" className="ConnectWalletModal-introduction__img" />
        <div className="flex flex-col">
          <Typography variant="large" className="ConnectWalletModal-introduction__subtitle">
            <Trans>Welcome to</Trans>
          </Typography>
          <Typography variant="h4" className="ConnectWalletModal-introduction__title">
            <Trans>Nectarliquid</Trans>
          </Typography>
        </div>
        <Typography variant="tiny" className="ConnectWalletModal-introduction__description">
          <Trans>
            Nectarliquid is a decentralized, wallet-native trading platform built on Asset Chain, offering seamless spot
            and perpetual futures trading (with up to 50x leverage)—all non-custodially, with no sign-ups or
            intermediaries.
          </Trans>
        </Typography>
      </div>
      <div className="ConnectWalletModal-content">
        <div className="ConnectWalletModal-content__header">
          <div className="flex flex-col items-start gap-[8px] max-w-[184px]">
            {qrWalletConnect ? (
              <div className="flex items-center gap-2">
                <ChevronLeft
                  className="w-[24px] h-[24px] cursor-pointer select-none"
                  onClick={() => setQrWalletConnect(null)}
                />
                <h6 className="ConnectWalletModal-content__title">
                  <Trans>Connect</Trans>
                </h6>
              </div>
            ) : (
              <h6 className="ConnectWalletModal-content__title">
                <Trans>Connect Wallet</Trans>
              </h6>
            )}
            <p className="ConnectWalletModal-content__description">
              {qrWalletConnect ? (
                <Trans>Scan with {qrWalletConnect.connector.rkDetails.name} to connect your wallet</Trans>
              ) : (
                <Trans>Connect your wallet with any of the supported dapp bellow</Trans>
              )}
            </p>
          </div>
          <Button
            variant="tertiary"
            className="ConnectWalletModal-close-button"
            onClick={() => setConnectWalletModalOpen(false)}
          >
            <CloseIcon />
          </Button>
        </div>
        {qrWalletConnect ? (
          <div className="ConnectWalletModal-qr-code-container">
            <QRCode
              value={qrWalletConnect.uri}
              className="ConnectWalletModal-qr-code"
              size={256}
              includeMargin={true}
              imageSettings={{
                src: qrWalletConnect.icon,
                height: 36,
                width: 36,
                excavate: true,
              }}
            />
            <div className="mt-8 flex items-center justify-between gap-2">
              <Typography variant="tiny">
                <Trans>Get {qrWalletConnect.connector.rkDetails.name}?</Trans>
              </Typography>
              <Button
                variant="primary"
                size="small"
                className="ConnectWalletModal-qr-code-button min-w-[60px]"
                onClick={() => {
                  window.open(getDownloadUrl(qrWalletConnect.connector), "_blank");
                }}
              >
                <Trans>Get</Trans>
              </Button>
            </div>
          </div>
        ) : (
          <div className="ConnectWalletModal-wallets">
            {isConnecting ? (
              <Loader />
            ) : (
              connectors
                ?.filter((connector) => !!(connector as Connector)?.rkDetails)
                ?.map((connector) => (
                  <WalletRow
                    key={(connector as Connector & { uid?: string }).uid ?? connector.id}
                    connector={connector as Connector}
                    setIsConnecting={setIsConnecting}
                    setQrWalletConnect={setQrWalletConnect}
                  />
                ))
            )}
          </div>
        )}
      </div>
    </Modal>
  );
};

export default ConnectWalletModal;

const WalletRow = ({
  connector,
  setIsConnecting,
  setQrWalletConnect,
}: {
  connector: Connector;
  setIsConnecting: (isConnecting: boolean) => void;
  setQrWalletConnect: (qrWalletConnect: { connector: Connector; uri: string; icon: string } | null) => void;
}) => {
  const { connectAsync } = useConnect();
  const [iconUrl, setIconUrl] = useState<string | undefined>(connector?.icon);

  useEffect(() => {
    if (!connector?.icon) {
      const getIconUrl = async () => {
        try {
          const url = await (connector as any)?.rkDetails?.iconUrl();
          setIconUrl(url);
        } catch (_error) {
          // eslint-disable-next-line no-console
          console.error("Failed to fetch icon URL for connector", connector.name);
        }
      };
      getIconUrl();
    }
  }, [connector]);

  if (connector?.rkDetails?.id === "walletConnect" && connector?.rkDetails?.isWalletConnectModalConnector) {
    return null;
  }

  return (
    <button
      onClick={async () => {
        setIsConnecting(true);
        try {
          if (!connector?.rkDetails?.installed) {
            const provider = await connector.getProvider();
            if (provider) {
              // @ts-expect-error provider event typing
              provider.once("display_uri", (uri: string) => {
                setQrWalletConnect({
                  connector: connector,
                  icon: iconUrl || "",
                  uri,
                });
                setIsConnecting(false);
              });
            } else {
              window.open(getDownloadUrl(connector), "_blank");
              return;
            }
          }
          await connectAsync({ connector });
          // AuthProvider opens the Sign-in modal after connect — do not auto-call /auth/nonce here.
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error("Failed to connect wallet:", error);
        } finally {
          setIsConnecting(false);
        }
      }}
      className="ConnectWalletModal-wallet-row"
    >
      {iconUrl && <img src={iconUrl} alt={`${connector.name} icon`} className="ConnectWalletModal-wallet-icon" />}
      <span className="ConnectWalletModal-wallet-name">{(connector as any)?.rkDetails?.name || connector?.name}</span>
    </button>
  );
};
